import { useEffect, useState } from 'react';
import { getCurrentCounter } from '../utils/timeCounter';

/**
 * Ticks down the number of whole seconds remaining until `expiryTime`.
 *
 * Recomputes from the timestamp on every tick (rather than decrementing a
 * local counter) so the value stays accurate even if the tab is backgrounded
 * or the interval drifts. Returns 0 once the expiry has passed.
 *
 * @param expiryTime ISO timestamp string, or null/undefined when inactive.
 */
export const useExpiryCountdown = (
  expiryTime: string | null | undefined
): number => {
  const [secondsLeft, setSecondsLeft] = useState(() =>
    expiryTime ? getCurrentCounter(expiryTime) : 0
  );

  useEffect(() => {
    if (!expiryTime) {
      setSecondsLeft(0);
      return;
    }

    // Sync immediately when the expiry changes (e.g. new batch arrives).
    setSecondsLeft(getCurrentCounter(expiryTime));

    const interval = setInterval(() => {
      const remaining = getCurrentCounter(expiryTime);
      setSecondsLeft(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiryTime]);

  return secondsLeft;
};

export default useExpiryCountdown;
